import React from 'react';
import { useOktaAuth } from '@okta/okta-react';
import { withLDProvider, useLDClient } from 'launchdarkly-react-client-sdk';

const clientSideID = import.meta.env.VITE_LAUNCHDARKLY_CLIENT_ID;

/**
 * Inner component that identifies the LaunchDarkly user
 * from the Okta ID token's subject claim once authentication is ready.
 */
function LDUserIdentifier({ children }) {
  const { authState } = useOktaAuth();
  const ldClient = useLDClient();

  React.useEffect(() => {
    if (ldClient && authState?.isAuthenticated && authState?.idToken?.claims?.sub) {
      ldClient.identify({
        kind: 'user',
        key: authState.idToken.claims.sub,
      });
    }
  }, [ldClient, authState]);

  return <>{children}</>;
}

/**
 * Wrap children with the LaunchDarkly provider.
 * While waiting for the LD client to initialise it renders children immediately
 * (flags will return their defaults until the client is ready).
 *
 * If no client-side ID is configured the provider is skipped entirely so
 * local development without LD still works.
 */
function LaunchDarklyProvider({ children }) {
  // Memoize the enhanced component so it is only created once
  const LDApp = React.useMemo(() => {
    if (!clientSideID) {
      // No LD key configured — render children as-is
      return ({ children }) => <>{children}</>;
    }

    return withLDProvider({
      clientSideID,
      reactOptions: {
        useCamelCaseFlagKeys: true,
      },
      // Start with an anonymous context; LDUserIdentifier will call
      // identify() once the Okta id token is available.
      context: {
        kind: 'user',
        anonymous: true,
        key: 'anonymous',
      },
    })(({ children }) => <LDUserIdentifier>{children}</LDUserIdentifier>);
  }, []);

  return <LDApp>{children}</LDApp>;
}

export default LaunchDarklyProvider;
